import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, logout } from '../lib/adminAPI';
import { ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useRowSelection, DeleteSelectedBar } from '../components/admin/RowSelection';
import { LeadStatusSelect, LEAD_STATUSES, normalizeStatus } from '../components/admin/LeadStatus';

const AdminContacts = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { selected, toggle, toggleAll, clear } = useRowSelection();

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      const data = await adminAPI.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Mobile', 'Street Address', 'Suburb', 'Postcode', 'Status', 'Looking For', 'Property Type', 'Timeframe', 'Message', 'Submitted At'];
    const csvContent = [
      headers.join(','),
      ...contacts.map(contact => [
        contact.fullName,
        contact.email,
        contact.mobile,
        contact.propertyAddress,
        contact.suburb || '',
        contact.postcode || '',
        normalizeStatus(contact.status),
        (contact.lookingFor || []).join('; '),
        contact.propertyType || '',
        contact.timeframe || '',
        contact.message || '',
        new Date(contact.submittedAt).toLocaleString()
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully!');
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    const { deleted } = await adminAPI.deleteContacts(ids);
    setContacts(prev => prev.filter(c => !selected.has(c.id)));
    clear();
    return deleted;
  };

  const handleStatusChange = (id, status) => {
    setContacts(prev => prev.map(c => (c.id === id ? { ...c, status } : c)));
  };

  const filteredContacts = contacts.filter(contact =>
    (statusFilter === 'all' || normalizeStatus(contact.status) === statusFilter) && (
      contact.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.mobile.includes(searchTerm) ||
      contact.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const statusCounts = contacts.reduce((acc, c) => {
    const key = normalizeStatus(c.status);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const visibleIds = filteredContacts.map(c => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));

  if (loading) {
    return <div className="admin-loading">Loading contacts...</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <button onClick={() => navigate('/admin/dashboard')} className="admin-back-btn">
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="admin-page-title">Contact Submissions</h1>
          <p className="admin-page-subtitle">{contacts.length} total submissions</p>
        </div>
        <div className="admin-header-actions">
          <button onClick={exportToCSV} className="admin-export-btn">
            <Download size={18} />
            Export CSV
          </button>
          <button onClick={logout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-search">
        <input
          type="text"
          placeholder="Search by name, email, phone, or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="admin-search-input"
          data-testid="contacts-search-input"
        />
        <div className="lead-status-filter" role="tablist" aria-label="Filter by status" data-testid="lead-status-filter">
          {[{ value: 'all', label: 'All' }, ...LEAD_STATUSES].map(s => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={statusFilter === s.value}
              className={`lead-status-filter-btn${statusFilter === s.value ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(s.value)}
              data-testid={`status-filter-${s.value}`}
            >
              {s.label}
              <span className="lead-status-filter-count">{s.value === 'all' ? contacts.length : (statusCounts[s.value] || 0)}</span>
            </button>
          ))}
        </div>
      </div>

      <DeleteSelectedBar count={selected.size} label="lead" onConfirm={deleteSelected} onClear={clear} />

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="admin-check-cell">
                <input
                  type="checkbox"
                  aria-label="Select all visible leads"
                  checked={allVisibleSelected}
                  onChange={() => toggleAll(visibleIds)}
                  data-testid="select-all-contacts"
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Property</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.length === 0 ? (
              <tr>
                <td colSpan="8" className="admin-table-empty">
                  {searchTerm || statusFilter !== 'all' ? 'No contacts match your filters' : 'No contacts yet'}
                </td>
              </tr>
            ) : (
              filteredContacts.map(contact => (
                <tr key={contact.id} className={selected.has(contact.id) ? 'admin-row-selected' : ''}>
                  <td className="admin-check-cell">
                    <input
                      type="checkbox"
                      aria-label={`Select ${contact.fullName}`}
                      checked={selected.has(contact.id)}
                      onChange={() => toggle(contact.id)}
                      data-testid={`select-contact-${contact.id}`}
                    />
                  </td>
                  <td className="admin-table-name">
                    {contact.fullName}
                    {contact.source === 'google_ads_lead_form' && (
                      <span className={`lead-source-badge${contact.isTest ? ' is-test' : ''}`} data-testid={`lead-source-${contact.id}`}>
                        {contact.isTest ? 'Google test' : 'Google Ads form'}
                      </span>
                    )}
                    {contact.source === 'chatbot' && (
                      <span className="lead-source-badge is-chat" data-testid={`lead-source-${contact.id}`}>Chat</span>
                    )}
                  </td>
                  <td>{contact.email}</td>
                  <td>{contact.mobile}</td>
                  <td>{contact.propertyAddress}{contact.suburb ? `, ${contact.suburb}` : ''}</td>
                  <td>
                    <LeadStatusSelect contactId={contact.id} status={contact.status} onChange={handleStatusChange} />
                  </td>
                  <td>{new Date(contact.submittedAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => navigate(`/admin/contacts/${contact.id}`)}
                      className="admin-view-btn"
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminContacts;
